import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetServiceRequestsQuery,
  useUpdateServiceRequestMutation,
  useUpdateServiceRequestStatusMutation,
} from '@/services/ServiceRequestManagement';
import {
  IServiceRequest,
  ServiceRequestUpdateData,
} from '@/types/service-request-management.types';
import { ServiceRequestEditModal } from './ServiceRequestEditModal';
import { ConfirmationModal } from '@/components/ConfirmationModal';

const formatCategoryDisplay = (category: string): string => {
  if (!category) return 'N/A';
  return category
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getStatusBadge = (status: string) => {
  const statusLower = status?.toLowerCase();
  switch (statusLower) {
    case 'pending':
      return <span className="badge badge-info">Pending</span>;
    case 'approved':
      return <span className="badge badge-success">Approved</span>;
    case 'rejected':
      return <span className="badge badge-danger">Rejected</span>;
    case 'in_progress':
      return <span className="badge badge-warning">In Progress</span>;
    case 'completed':
      return <span className="badge badge-success">Completed</span>;
    default:
      return (
        <span className="badge badge-secondary">
          {formatCategoryDisplay(status)}
        </span>
      );
  }
};

const ServiceRequestListings = ({ harborId }: { harborId: string | null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedServiceRequest, setSelectedServiceRequest] =
    useState<IServiceRequest | null>(null);
  const [formData, setFormData] = useState<ServiceRequestUpdateData>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reject confirmation modal state
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectServiceRequest, setRejectServiceRequest] =
    useState<IServiceRequest | null>(null);

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [updateServiceRequest, { isLoading: isUpdating }] =
    useUpdateServiceRequestMutation();
  const [updateServiceRequestStatus, { isLoading: isRejecting }] =
    useUpdateServiceRequestStatusMutation();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
      harborId: harborId || undefined,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue, harborId]
  );

  // Don't fetch if harborId is not provided

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch: refetchServiceRequests,
  } = useGetServiceRequestsQuery(queryParams);

  useEffect(() => {
    if (typeof refetchServiceRequests === 'function') {
      refetchServiceRequests();
    }
  }, [debouncedValue, refetchServiceRequests]);

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  const rows: IServiceRequest[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data;
  }, [data]);

  const handleOpenEditModal = useCallback((serviceRequest: IServiceRequest) => {
    setSelectedServiceRequest(serviceRequest);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedServiceRequest(null);
    setFormData({});
    setErrors({});
  }, []);

  const handleOpenApproveModal = useCallback(
    (serviceRequest: IServiceRequest) => {
      // Navigate to service create page with pre-populated data from service request
      const params = new URLSearchParams();
      if (serviceRequest.harborId) {
        params.append('harborId', serviceRequest.harborId);
      }
      params.append('showDistanceFromHarbor', 'true');
      params.append('serviceRequestId', serviceRequest.id);
      params.append('name', serviceRequest.name || '');
      params.append('description', serviceRequest.description || '');
      params.append('category', serviceRequest.category || '');
      params.append('hideServiceProvider', 'true');

      navigate(`/services/create?${params.toString()}`);
    },
    [navigate]
  );

  const handleOpenRejectModal = useCallback(
    (serviceRequest: IServiceRequest) => {
      setRejectServiceRequest(serviceRequest);
      setIsRejectModalOpen(true);
    },
    []
  );

  const handleCloseRejectModal = useCallback(() => {
    setIsRejectModalOpen(false);
    setRejectServiceRequest(null);
  }, []);

  const handleRejectConfirm = useCallback(
    async (reason?: string) => {
      if (!rejectServiceRequest?.id) return;

      try {
        await updateServiceRequestStatus({
          id: rejectServiceRequest.id,
          data: {
            status: 'rejected',
            reason: reason,
          },
          harborId: rejectServiceRequest.harborId ?? undefined,
        }).unwrap();

        enqueueSnackbar('Service request rejected successfully', {
          variant: 'success',
        });
        handleCloseRejectModal();
        refetchServiceRequests();
      } catch (error) {
        let errorMessage = 'Failed to reject service request';

        if (error && typeof error === 'object') {
          const rtkError = error as { data?: unknown; message?: string };

          if (
            rtkError.data &&
            typeof rtkError.data === 'object' &&
            rtkError.data !== null
          ) {
            const errorData = rtkError.data as {
              data?: { message?: string };
              message?: string;
            };

            if (
              errorData.data &&
              typeof errorData.data === 'object' &&
              'message' in errorData.data &&
              typeof errorData.data.message === 'string'
            ) {
              errorMessage = errorData.data.message;
            } else if (
              'message' in errorData &&
              typeof errorData.message === 'string'
            ) {
              errorMessage = errorData.message;
            }
          } else if (
            'message' in rtkError &&
            typeof rtkError.message === 'string'
          ) {
            errorMessage = rtkError.message;
          }
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      rejectServiceRequest,
      updateServiceRequestStatus,
      enqueueSnackbar,
      handleCloseRejectModal,
      refetchServiceRequests,
    ]
  );

  const handleSubmitEdit = useCallback(
    async (data: ServiceRequestUpdateData) => {
      if (!selectedServiceRequest) return;

      // Basic validation
      const newErrors: { [key: string]: string } = {};
      if (!data.name?.trim()) {
        newErrors.name = 'Name is required';
      }
      if (!data.category) {
        newErrors.category = 'Category is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      try {
        await updateServiceRequest({
          id: selectedServiceRequest.id,
          data: data,
          harborId: selectedServiceRequest.harborId ?? undefined,
        }).unwrap();

        enqueueSnackbar('Service request updated successfully', {
          variant: 'success',
        });
        handleCloseEditModal();
        refetchServiceRequests();
      } catch (error) {
        let errorMessage = 'Failed to update service request';

        if (error && typeof error === 'object') {
          // RTK Query error structure: { status: number, data: ErrorApiResponse | unknown }
          const rtkError = error as { data?: unknown; message?: string };

          if (
            rtkError.data &&
            typeof rtkError.data === 'object' &&
            rtkError.data !== null
          ) {
            // Check if error.data is ErrorApiResponse (has nested data.message)
            const errorData = rtkError.data as {
              data?: { message?: string };
              message?: string;
            };

            if (
              errorData.data &&
              typeof errorData.data === 'object' &&
              'message' in errorData.data &&
              typeof errorData.data.message === 'string'
            ) {
              // ErrorApiResponse structure: error.data.data.message
              errorMessage = errorData.data.message;
            } else if (
              'message' in errorData &&
              typeof errorData.message === 'string'
            ) {
              // Direct message in error.data.message
              errorMessage = errorData.message;
            }
          } else if (
            'message' in rtkError &&
            typeof rtkError.message === 'string'
          ) {
            // Direct message in error.message
            errorMessage = rtkError.message;
          }
        }

        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    },
    [
      selectedServiceRequest,
      updateServiceRequest,
      enqueueSnackbar,
      handleCloseEditModal,
      refetchServiceRequests,
    ]
  );

  const hasPendingRequests = useMemo(
    () => rows.some((request) => request.status?.toLowerCase() === 'pending'),
    [rows]
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessor: (row: IServiceRequest) => row.name,
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceRequest } }) => (
          <span
            className="font-medium block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.name}
          >
            {row.original.name}
          </span>
        ),
      },
      {
        accessor: (row: IServiceRequest) => row.category,
        id: 'category',
        header: 'Category',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceRequest } }) => (
          <span className="text-gray-800 font-normal">
            {formatCategoryDisplay(row.original.category)}
          </span>
        ),
      },
      {
        accessor: (row: IServiceRequest) => row.description,
        id: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceRequest } }) => (
          <span
            className="text-gray-800 font-normal block"
            style={{
              minWidth: 0,
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.description}
          >
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessor: (row: IServiceRequest) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceRequest } }) =>
          getStatusBadge(row.original.status),
      },
    ];

    if (hasPendingRequests) {
      baseColumns.push({
        accessor: (row: IServiceRequest) => row.id,
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IServiceRequest } }) => {
          if (row.original.status?.toLowerCase() !== 'pending') {
            return <></>;
          }

          return (
            <div className="flex gap-2">
              {hasPermission('service_request_update') && (
                <button
                  className="btn btn-sm btn-icon btn-light"
                  onClick={() => handleOpenEditModal(row.original)}
                  title="Edit"
                >
                  <KeenIcon icon="pencil" className="text-base" />
                </button>
              )}
              {hasPermission('service_request_approve') && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleOpenApproveModal(row.original)}
                  title="Approve"
                  aria-label={`Approve service request ${row.original.name}`}
                >
                  <KeenIcon icon="check" className="text-base" />
                </button>
              )}
              {hasPermission('service_request_approve') && (
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleOpenRejectModal(row.original)}
                  title="Reject"
                  aria-label={`Reject service request ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [
    handleOpenEditModal,
    handleOpenApproveModal,
    hasPendingRequests,
    handleOpenRejectModal,
    hasPermission,
  ]);

  const resetFilters = () => {
    if (searchTerm) {
      resetSearch();
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <>
      <div className="card card-grid w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full justify-between">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0" role="search">
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center gap-2"
                  htmlFor="search-service-requests"
                  aria-label="Search service requests"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                    aria-hidden="true"
                  />
                  <input
                    id="search-service-requests"
                    type="text"
                    placeholder="Search service requests..."
                    className="w-full bg-transparent border-0 outline-none focus:ring-0"
                    value={searchTerm}
                    onChange={handleSearch}
                    aria-label="Search service requests"
                  />
                </label>
              </div>
              {searchTerm && (
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={resetFilters}
                  aria-label="Reset filters"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div
              className="text-center py-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              Loading service requests...
            </div>
          ) : isError ? (
            <div
              className="text-center py-4 text-red-500"
              role="alert"
              aria-live="assertive"
            >
              Failed to load service requests. Please try again.
            </div>
          ) : (
            <div className="service-request-grid-wrapper">
              <DataGrid<IServiceRequest>
                columns={columns}
                data={rows}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                serverSide={true}
                onFilterReset={resetFilters}
                hasActiveFilters={!!searchTerm}
                aria-label="Service request listings table"
              />
            </div>
          )}
        </div>
      </div>

      <ServiceRequestEditModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        serviceRequest={selectedServiceRequest}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSubmitEdit}
        isSubmitting={isUpdating}
      />

      <ConfirmationModal
        open={isRejectModalOpen}
        onClose={handleCloseRejectModal}
        title="Reject Service Request"
        message={`Are you sure you want to reject the service request "${rejectServiceRequest?.name || ''}"?`}
        onConfirm={handleRejectConfirm}
        confirmLabel={isRejecting ? 'Rejecting...' : 'Reject'}
        cancelLabel="Cancel"
        showReasonInput={true}
        confirmButtonVariant="danger"
        disabled={isRejecting}
      />
    </>
  );
};

export { ServiceRequestListings };
